import fetchAPI from '../../../utils/fetchApi';
import { getSessionStorageItem, setSessionStorageItem } from '../../../utils/sStorage';
import formatFeedItemsUrl from './utils/formatFeedItemsUrl';
import generateFeedItemsParams from './utils/generateFeedItemsParams';
import { trackGAEvent } from '../../../utils/gaTracking';
import { TFeed, TFeedItem } from 'homepage-web-bff/types/TFeeds';
import createFavorite from '../repositories/createFavorite';
import wasPageReloaded from '../../../utils/wasPageReloaded';

type TCachedFeed = { feedItemsPage: number; feedItems: Record<number, TFeedItem[]> };

const isValidFeedItem = (item: unknown): item is TFeedItem =>
  item !== null &&
  typeof item === 'object' &&
  typeof (item as Record<string, unknown>).itemId === 'string' &&
  (item as Record<string, unknown>).itemId !== '' &&
  typeof (item as Record<string, unknown>).url === 'string';

const readCache = (key: string, ttl?: number): TCachedFeed | null => {
  const raw = getSessionStorageItem(key, ttl);
  if (raw === null || typeof raw !== 'object') return null;
  const d = raw as Record<string, unknown>;
  if (typeof d.feedItemsPage !== 'number') return null;
  if (d.feedItems === null || typeof d.feedItems !== 'object' || Array.isArray(d.feedItems)) return null;
  const filtered: Record<number, TFeedItem[]> = {};
  let hasValidItems = false;
  for (const [pageKey, pageItems] of Object.entries(d.feedItems as object)) {
    if (Array.isArray(pageItems)) {
      const validItems = (pageItems as unknown[]).filter(isValidFeedItem) as TFeedItem[];
      filtered[Number(pageKey)] = validItems;
      if (validItems.length > 0) hasValidItems = true;
    }
  }
  if (!hasValidItems) return null;
  return { feedItemsPage: d.feedItemsPage, feedItems: filtered };
};

const HOMEPAGE_FEED = 'HOMEPAGE_FEED';

type TGetFeedItemsProps = {
  feed: TFeed;
  page: number;
  feedPostcode?: string;
  favoriteAdIds?: string[];
  // Own, short-lived cache entry (instead of the shared 6 hour one) for feeds that are part of an experiment.
  experimentCache?: TExperimentCache;
};

type TExperimentCache = { keySuffix: string; ttlMs: number };

// Groups B and C of the Albatross experiment (MP27689): their own cache entry, so it never mixes with the regular
// feed, expiring after 2 minutes so a group change shows quickly while back navigation keeps the same feed.
export const ALBATROSS_FEED_CACHE: TExperimentCache = { keySuffix: 'MP27689B', ttlMs: 2 * 60 * 1000 };

const minutes = 60;
const seconds = 60;
const milliseconds = 1000;
const ttlInHours = 6;
// convert hours to milliseconds
const cacheTtl = ttlInHours * minutes * seconds * milliseconds;

const formatCacheKey = ({ feedType, postcode }): string =>
  postcode ? `${HOMEPAGE_FEED}_${feedType}_${postcode}_V2` : `${HOMEPAGE_FEED}_${feedType}_V2`;

const checkIfFavoriteItem = async (feedItems: TFeedItem[], xsrfToken) => {
  const favItem = getSessionStorageItem('FAVORITE_ITEM');
  if (favItem) {
    trackGAEvent('WatchlistAddBegin');
    const response = await createFavorite(favItem.itemId, xsrfToken);
    if (response && response.isFavorite) {
      trackGAEvent('WatchlistAddSuccess');
    }
    // remove from session storage
    sessionStorage.removeItem(`FAVORITE_ITEM_CREATED`);
    sessionStorage.removeItem(`FAVORITE_ITEM_VALUE`);
    const updatedFeedItems = feedItems.map((feed) => {
      return feed.itemId === favItem.itemId ? { ...feed, isFavorited: true } : feed;
    });
    return updatedFeedItems;
  }
  return feedItems;
};

export default async function getFeedItems(
  { feed, feedPostcode, page, favoriteAdIds, experimentCache }: TGetFeedItemsProps,
  userLoggedIn,
  xsrfToken,
): Promise<TFeedItem[]> {
  const params = generateFeedItemsParams({ feed, feedPostcode, page });

  const baseStorageKey = formatCacheKey(params);
  const storageKey = experimentCache ? `${baseStorageKey}_${experimentCache.keySuffix}` : baseStorageKey;
  // An explicit browser refresh reloads the experiment feed and restarts its cache time. Back and forward
  // navigation does not: wasPageReloaded() is only true for the first check after a refresh.
  if (experimentCache && wasPageReloaded()) {
    sessionStorage.removeItem(`${storageKey}_CREATED`);
    sessionStorage.removeItem(`${storageKey}_VALUE`);
  }
  const cachedData = readCache(storageKey, experimentCache?.ttlMs ?? cacheTtl);
  if (cachedData !== null && cachedData.feedItemsPage >= page) {
    if (wasPageReloaded() && userLoggedIn) {
      const favoriteIds = new Set(favoriteAdIds ?? []);
      const patchedPages: Record<number, TFeedItem[]> = {};
      for (let p = 0; p <= cachedData.feedItemsPage; p++) {
        patchedPages[p] = (cachedData.feedItems[p] ?? []).map((item: TFeedItem) => ({
          ...item,
          isFavorited: favoriteIds.has(item.itemId),
        }));
      }
      setSessionStorageItem(storageKey, { ...cachedData, feedItems: patchedPages });
      const pageItems = patchedPages[page] ?? [];
      if (xsrfToken && page === 0) {
        return checkIfFavoriteItem(pageItems, xsrfToken);
      }
      return pageItems;
    }

    const pageItems = cachedData.feedItems[page] ?? [];
    if (userLoggedIn) {
      return checkIfFavoriteItem(pageItems, xsrfToken);
    }
    return pageItems;
  }

  try {
    let feedItems = await fetchAPI({ url: formatFeedItemsUrl(params), preloadCompatible: true });

    if (feedItems?.length) {
      if (userLoggedIn) {
        feedItems = await checkIfFavoriteItem(feedItems, xsrfToken);
      }
      setSessionStorageItem(storageKey, {
        feedItemsPage: page,
        feedItems: { ...cachedData?.feedItems, [page]: feedItems },
      });
    }

    return feedItems;
  } catch {
    return [];
  }
}
